"use client";

/**
 * Adresowanie widoków pluginu `c_crm_project_management` w URL.
 *
 * Shell konta routuje zakładki hashem `#extension-{action_id}`, a
 * `ProfileTabs.getExtensionActionFromHash` obsługuje hash rozdzielony PRZECINKAMI
 * (wzorzec ILINK dla anchor) i bierze pierwszy człon z prefiksem `extension-`.
 * Korzystamy z tego: stan wewnętrzny pluginu jedzie jako DODATKOWY człon, więc
 * shell go po prostu ignoruje i nie trzeba go modyfikować.
 *
 * Gramatyka członu (prefiks `pm-` chroni przed kolizją z innymi wtyczkami):
 *
 * | Widok                      | Hash                                        |
 * |----------------------------|---------------------------------------------|
 * | lista projektów            | `#extension-projects`                       |
 * | zadania projektu 128       | `#extension-projects,pm-p128-tasks`         |
 * | formularz zgłoszenia       | `#extension-projects,pm-p128-add`           |
 * | szczegóły zadania 456      | `#extension-projects,pm-p128-t456`          |
 * | panel usługi 4412          | `#extension-services,pm-s4412`              |
 * | dziennik pracy projektu 128| `#extension-worklog,pm-w128`                |
 *
 * ID projektu jest obecne TAKŻE w adresie szczegółów zadania - dzięki temu powrót
 * i nagłówek działają po wejściu z linku, bez dodatkowego zapytania o to, do
 * którego projektu zadanie należy.
 *
 * Nawigacja idzie przez `location.hash`, więc przyciski Wstecz/Dalej przeglądarki
 * działają out of the box, a każdy widok da się skopiować i wysłać dalej. Dostęp
 * i tak weryfikuje backend przy każdym zapytaniu - link do cudzego zadania
 * skończy się błędem 404, nie podglądem.
 */

export type PluginRoute =
  | { view: "projects" }
  | { view: "service"; serviceId: number }
  | { view: "worklog"; projectId: number }
  | { view: "tasks"; projectId: number }
  | { view: "add"; projectId: number }
  | { view: "detail"; projectId: number; taskId: number };

/** Prefiks członu hasha należącego do tej wtyczki. */
const PIECE_PREFIX = "pm-";

/** `pm-p{projectId}-tasks` | `pm-p{projectId}-add` | `pm-p{projectId}-t{taskId}` */
const PIECE_RE = /^pm-p(\d+)-(tasks|add|t(\d+))$/;

/** `pm-s{serviceId}` - panel pojedynczej uslugi (zakladka `services`). */
const SERVICE_RE = /^pm-s(\d+)$/;

/** `pm-w{projectId}` - dziennik pracy zawężony do projektu (zakładka `worklog`). */
const WORKLOG_RE = /^pm-w(\d+)$/;

/**
 * Odczytuje widok wtyczki z hasha adresu.
 *
 * Nieznany albo uszkodzony człon (ręcznie zmieniony link, stary bookmark) daje
 * widok listy projektów - to bezpieczne wyjście, a nie ciche udawanie, że
 * użytkownik jest gdzie indziej.
 *
 * @param hash Wartość `location.hash` (z `#` albo bez)
 */
export function parsePluginRoute(hash: string): PluginRoute {
  const stripped = hash.startsWith("#") ? hash.slice(1) : hash;

  for (const piece of stripped.split(",")) {
    if (!piece.startsWith(PIECE_PREFIX)) continue;

    const serviceMatch = SERVICE_RE.exec(piece);
    if (serviceMatch) {
      const serviceId = Number(serviceMatch[1]);
      if (Number.isFinite(serviceId) && serviceId > 0) return { view: "service", serviceId };
      continue;
    }

    const workLogMatch = WORKLOG_RE.exec(piece);
    if (workLogMatch) {
      const projectId = Number(workLogMatch[1]);
      if (Number.isFinite(projectId) && projectId > 0) return { view: "worklog", projectId };
      continue;
    }

    const match = PIECE_RE.exec(piece);
    if (!match) continue;

    const projectId = Number(match[1]);
    if (!Number.isFinite(projectId) || projectId <= 0) continue;

    if (match[2] === "tasks") return { view: "tasks", projectId };
    if (match[2] === "add") return { view: "add", projectId };

    const taskId = Number(match[3]);
    if (Number.isFinite(taskId) && taskId > 0) return { view: "detail", projectId, taskId };
  }

  return { view: "projects" };
}

/** Buduje człon hasha dla widoku. `null` dla listy projektów (brak dodatkowego członu). */
function buildPiece(route: PluginRoute): string | null {
  switch (route.view) {
    case "tasks":
      return `${PIECE_PREFIX}p${route.projectId}-tasks`;
    case "add":
      return `${PIECE_PREFIX}p${route.projectId}-add`;
    case "detail":
      return `${PIECE_PREFIX}p${route.projectId}-t${route.taskId}`;
    case "service":
      return `${PIECE_PREFIX}s${route.serviceId}`;
    case "worklog":
      return `${PIECE_PREFIX}w${route.projectId}`;
    default:
      return null;
  }
}

/**
 * Buduje pełny hash dla widoku, ZACHOWUJĄC człony należące do shella
 * (`extension-...`) i innych wtyczek - podmieniany jest wyłącznie człon `pm-`.
 *
 * @param currentHash Bieżący `location.hash`
 * @param route       Docelowy widok
 */
export function buildPluginHash(currentHash: string, route: PluginRoute): string {
  const stripped = currentHash.startsWith("#") ? currentHash.slice(1) : currentHash;

  const kept = stripped
    .split(",")
    .filter((piece) => piece !== "" && !piece.startsWith(PIECE_PREFIX));

  const piece = buildPiece(route);
  if (piece) kept.push(piece);

  return kept.length > 0 ? `#${kept.join(",")}` : "#";
}

/**
 * Przechodzi do widoku przez zmianę hasha (wpis w historii przeglądarki).
 *
 * Świadomie NIE używamy routera Next.js: zmiana hasha nie przeładowuje Server
 * Components, więc panel przełącza widok bez pełnego renderu strony - to samo
 * założenie, na którym stoi przełączanie zakładek w `ProfileTabs`.
 */
export function navigateToPluginRoute(route: PluginRoute): void {
  if (typeof window === "undefined") return;
  window.location.hash = buildPluginHash(window.location.hash, route);
}

/** Adres bezwzględny widoku - do skopiowania i wysłania dalej. */
export function pluginRouteHref(route: PluginRoute): string {
  if (typeof window === "undefined") return "";
  const { origin, pathname, search } = window.location;
  return `${origin}${pathname}${search}${buildPluginHash(window.location.hash, route)}`;
}

/** Prefiks członu hasha, którym shell konta wybiera zakładkę. */
const EXTENSION_PREFIX = "extension-";

/**
 * Hash zakładki Dziennik pracy zawężonej do projektu - link z listy projektów.
 *
 * Zmienia DWA człony naraz: zakładkę shella (`extension-projects` -> `extension-worklog`)
 * i człon wtyczki (`pm-w{projectId}`). Człony innych wtyczek zostają nietknięte.
 *
 * @param currentHash Bieżący `location.hash`
 * @param projectId   ID projektu, do którego zawęzić dziennik
 */
export function buildWorkLogHash(currentHash: string, projectId: number): string {
  const stripped = currentHash.startsWith("#") ? currentHash.slice(1) : currentHash;

  const kept = stripped
    .split(",")
    .filter(
      (piece) =>
        piece !== "" && !piece.startsWith(PIECE_PREFIX) && !piece.startsWith(EXTENSION_PREFIX),
    );

  return `#${[`${EXTENSION_PREFIX}worklog`, ...kept, `${PIECE_PREFIX}w${projectId}`].join(",")}`;
}

/** Adres bezwzględny dziennika pracy projektu - do atrybutu `href` linku. */
export function workLogHref(projectId: number): string {
  if (typeof window === "undefined") return "";
  const { origin, pathname, search } = window.location;
  return `${origin}${pathname}${search}${buildWorkLogHash(window.location.hash, projectId)}`;
}

/** Przejście do dziennika pracy projektu (zmiana zakładki shella + filtr projektu). */
export function navigateToWorkLog(projectId: number): void {
  if (typeof window === "undefined") return;
  window.location.hash = buildWorkLogHash(window.location.hash, projectId);
}
